/**
 * Enterprise UX Diagnostic Collectors.
 * 
 * Provides:
 * 1. Main-Thread Freeze & Long-Task Observers (detects UI unresponsive / page stuck).
 * 2. Resource & Asset Load Error Interceptor (scripts, CSS stylesheets, images, chunk load failures).
 * 3. DOM Mutation Pulse (verifies if clicks produced UI state changes to eliminate false-positive dead clicks).
 */

import type { SessionEvent } from "../core/types"
import { currentPath } from "./collectors"

function now(): string {
  return new Date().toISOString()
}

/**
 * Observes main-thread long tasks and thread lockup freezes.
 */
export function observeThreadFreezes(emit: (event: SessionEvent) => void): () => void {
  if (typeof window === "undefined") return () => {}

  let active = true
  let observer: PerformanceObserver | null = null

  // 1. Long Task Performance Observer (> 500ms blocking tasks)
  if (typeof PerformanceObserver !== "undefined") {
    try {
      if (PerformanceObserver.supportedEntryTypes?.includes("longtask")) {
        observer = new PerformanceObserver((list) => {
          if (!active) return
          for (const entry of list.getEntries()) {
            if (entry.duration >= 500) {
              const durMs = Math.round(entry.duration)
              const container = (entry as any).attribution?.[0]?.containerName || ""
              const src = (entry as any).attribution?.[0]?.containerSrc || ""
              emit({
                type: "page_stuck",
                ts: now(),
                target: container || src || "main-thread",
                path: currentPath(),
                text: `Main thread blocked for ${durMs}ms`,
                meta: {
                  duration_ms: durMs,
                  container,
                  src,
                },
              })
            }
          }
        })
        observer.observe({ entryTypes: ["longtask"] })
      }
    } catch {
      // Longtask observer not supported or restricted
    }
  }

  // 2. Main-Thread Heartbeat Detector (detects complete UI freeze >= 1500ms)
  let lastHeartbeat = performance.now()
  let heartbeatTimer: number | null = null

  const checkHeartbeat = () => {
    if (!active) return
    const current = performance.now()
    const gap = current - lastHeartbeat
    lastHeartbeat = current

    // If more than 1500ms elapsed for a 500ms expected tick, thread was frozen
    if (gap >= 1500) {
      const freezeDuration = Math.round(gap)
      emit({
        type: "freeze",
        ts: now(),
        target: "window",
        path: currentPath(),
        text: `UI frozen for ${freezeDuration}ms`,
        meta: {
          duration_ms: freezeDuration,
          expected_interval_ms: 500,
        },
      })
    }

    heartbeatTimer = window.setTimeout(checkHeartbeat, 500)
  }

  heartbeatTimer = window.setTimeout(checkHeartbeat, 500)

  return () => {
    active = false
    if (observer) {
      try {
        observer.disconnect()
      } catch {}
    }
    if (heartbeatTimer !== null) {
      clearTimeout(heartbeatTimer)
    }
  }
}

/**
 * Intercepts asset load errors (scripts, stylesheets, images) and unhandled promise rejections.
 */
export function observeAssetErrors(emit: (event: SessionEvent) => void): () => void {
  if (typeof window === "undefined") return () => {}

  // Capturing error listener for DOM elements (scripts, links, img)
  const handleResourceError = (event: ErrorEvent | Event) => {
    const target = event.target as HTMLElement | null
    if (!target || target === (window as any) || target === (document as any)) return

    const tag = target.tagName?.toLowerCase() || ""
    if (tag === "script" || tag === "link" || tag === "img") {
      const src = (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || ""
      const cleanSrc = src ? src.split("?")[0] : ""
      emit({
        type: "asset_error",
        ts: now(),
        target: `${tag}: ${cleanSrc || "unknown"}`,
        path: currentPath(),
        text: `Failed to load ${tag}: ${cleanSrc || "resource"}`,
        meta: {
          tag,
          src: cleanSrc,
        },
      })
    }
  }

  window.addEventListener("error", handleResourceError, true)

  return () => {
    window.removeEventListener("error", handleResourceError, true)
  }
}

/**
 * Emits a lightweight ui_state event when a click causes DOM mutations,
 * preventing false dead-click classifications for client-side widgets (menus, modals, accordions).
 */
export function observeDomMutations(emit: (event: SessionEvent) => void): () => void {
  if (typeof window === "undefined" || typeof MutationObserver === "undefined") return () => {}

  let lastClickTime = 0
  let pulseEmittedForClick = false

  const handleClick = () => {
    lastClickTime = performance.now()
    pulseEmittedForClick = false
  }

  const observer = new MutationObserver((mutations) => {
    const timeSinceClick = performance.now() - lastClickTime
    if (timeSinceClick <= 350 && !pulseEmittedForClick && mutations.length > 0) {
      // Meaningful mutation occurred shortly after click
      pulseEmittedForClick = true
      emit({
        type: "ui_state",
        ts: now(),
        target: "DOM",
        path: currentPath(),
        text: "UI state updated",
        meta: {
          mutation_count: mutations.length,
        },
      })
    }
  })

  try {
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-expanded", "aria-hidden", "data-state", "open", "hidden"],
    })
  } catch {}

  window.addEventListener("click", handleClick, { capture: true, passive: true })

  return () => {
    observer.disconnect()
    window.removeEventListener("click", handleClick, { capture: true })
  }
}
